import { useState } from 'react'
import { Grid, Typography } from '@material-ui/core'
import router, { useRouter } from 'next/router'
import { FormattedMessage, useIntl } from 'react-intl'
import dynamic from 'next/dynamic'
import { useFormikContext } from 'formik'
import DownloadTemplate from '@src/routes/Configurators/products/TubesMTORedesign/DownloadTemplate'
import CircleLoader from '@src/components/CircleLoader'
import { FileFormat } from '@src/routes/Configurators/products/TubesMTORedesign/enum'
import CautionPopupMTORedesign from '@src/routes/Configurators/components/CautionPopupMTORedesign'
import {
  SemiConfigFormValues,
  useProductFromQueryParam,
  getProductBasedDownloadUrl,
  fileHeaders,
  headerPosition,
} from './ProductsConfiguration'
import useSearch from './useSearch'
import { ActiveLinkType, SemiConfigProductTypes, uploadErrorType } from './enum'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import PageError from './PageError'
//file upload util does not work with ssr. this is to ensure we only render on client side
const UploadTemplate = dynamic(
  () => import('@src/routes/Configurators/components/UploadTemplate'),
  { ssr: false }
)
interface TermInput {
  term: string
}
const MultipleProductEntry = () => {
  const intl = useIntl()
  const formik = useFormikContext<SemiConfigFormValues>()
  const product = useProductFromQueryParam()
  const { searchQuery } = useSearch()
  const { query } = useRouter()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [fileData, setFileData] = useState<any>('')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const parseSpreadsheet = async (file?: File): Promise<string[] | string> =>
    new Promise((resolve) => {
      if (!file) {
        return resolve(uploadErrorType.UNABLE_TO_PROCESS_FILE)
      }
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const XLSX = await import('xlsx')
          const data = new Uint8Array((e.target as any).result)
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const hasHeaders =
            worksheet[headerPosition[product]]?.v === fileHeaders[product]

          if (hasHeaders) {
            const fileData: TermInput[] = XLSX.utils.sheet_to_json(worksheet, {
              header: ['term'],
            })
            const searchItems =
              product === SemiConfigProductTypes.PREDESIGNED_CRISPR_GRNA
                ? fileData.slice(1)
                : fileData.slice(2)
            if (searchItems.length === 0) {
              resolve(uploadErrorType.EMPTY_FILE)
            } else if (searchItems.length > 100) {
              resolve(uploadErrorType.MAXIMUM_NUMBER_OF_KEYS)
            } else {
              resolve(searchItems.map((data) => data?.term?.toString()))
            }
          } else {
            resolve(uploadErrorType.INVALID_TEMPLATE)
          }
        } catch (err) {
          resolve(uploadErrorType.UNABLE_TO_PROCESS_FILE)
        }
      }
      reader.readAsArrayBuffer(file)
    })

  const useDataFromFile = async (file: File) => {
    setErrorMessage('')
    formik.setFieldValue('searchData.searchKey', '', false)
    formik.setFieldValue('completedStep', 0, false)
    formik.setFieldValue('searchData.searchErrorMessage', '', false)
    if (
      FileFormat.AcceptFormat &&
      FileFormat.AcceptFormat.split(',')
        .map((type) => type.trim().toLowerCase())
        .includes(file?.type)
    ) {
      const fileInput: string[] | string = await parseSpreadsheet(file)
      if (
        fileInput === uploadErrorType.EMPTY_FILE ||
        fileInput === uploadErrorType.UNABLE_TO_PROCESS_FILE
      ) {
        setErrorMessage(
          intl.formatMessage({
            id: 'CONF_EMPTY_FILE',
            defaultMessage: 'The uploaded file is empty.',
          })
        )
      } else if (fileInput === uploadErrorType.INVALID_TEMPLATE) {
        setErrorMessage(
          intl.formatMessage({
            id: 'CONF_INVALID_TEMPLATE',
            defaultMessage:
              'The file you have uploaded is invalid. Try again using the template provided.',
          })
        )
      } else if (fileInput === uploadErrorType.MAXIMUM_NUMBER_OF_KEYS) {
        setErrorMessage(
          intl.formatMessage({
            id: 'CONF_UPLOAD_ERROR_MSG',
            defaultMessage: 'Maximum number of items per session is 100.',
          })
        )
      } else {
        // formik.setFieldValue('searchData.searchKey', fileInput.join(" "), false)
        //call query and redirect if success
        const response = await searchQuery({
          searchKey: Array.isArray(fileInput) ? fileInput.join(' ') : '',
          isUpload: true,
        })
        if (response && typeof response === 'string') {
          setErrorMessage(response)
        } else {
          //redirect to step 2
          formik.setFieldValue('completedStep', 1, false)
          router.push(
            {
              pathname: router.pathname,
              query: {
                country: query.country,
                language: query.language,
                activeLink: ActiveLinkType.ItemSelection,
              },
            },
            undefined,
            { shallow: true }
          )
        }
      }
      setLoading(false)
    } else {
      setErrorMessage(
        intl.formatMessage({
          id: 'CONF_INVALID_FORMAT',
          defaultMessage:
            'Invalid format. Please check the template and try again.',
        })
      )
      setLoading(false)
    }
  }
  const handleFileUpload = async (file: File) => {
    setLoading(true)
    if (formik.values.selectedClones.length > 0) {
      setFileData(file)
      setDialogOpen(true)
    } else {
      await useDataFromFile(file)
    }
  }

  const handleClose = async () => {
    setLoading(false)
    setDialogOpen(false)
    setFileData('')
  }

  const handleContinue = async () => {
    setDialogOpen(false)
    setLoading(true)
    await useDataFromFile(fileData)
  }
  const downloadUrl = getProductBasedDownloadUrl(product)
  return (
    <Grid container spacing={6} style={{ marginTop: '11px' }}>
      <PageError showError={errorMessage !== ''} />
      <Grid item xs={12}>
        <FormattedMessage
          id="CONF_OPTIONAL_INTR_MOD_TEMPLATE_standard"
          defaultMessage="Follow these steps below to quickly upload multiple entries using a pre-defined template."
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={4}>
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">
                <FormattedMessage
                  id="CONF_DNAPLATES_STEP1"
                  defaultMessage="Step 1"
                />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage
                id="DOWNLOAD_SEMI_CONFIG_ORDERING_TEMPLATE"
                defaultMessage="Download the template"
              />
              {':'}
            </Grid>
          </Grid>
          <Grid container item spacing={3}>
            <Grid item xs={12}>
              <DownloadTemplate
                label={intl.formatMessage({
                  id: 'DOWNLOAD_TEMPLATE',
                  defaultMessage: 'Download Template',
                })}
                isPcr={false}
                url={downloadUrl}
                product={product}
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">
                <FormattedMessage
                  id="CONF_DNAPLATES_STEP2"
                  defaultMessage="Step 2"
                />
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormattedMessage
                id="CONF_MULTIPLE_ENTRY_STEP_2"
                defaultMessage="Enter your specifications in the provided spreadsheet template."
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h2">
                <FormattedMessage
                  id="CONF_DNAPLATES_STEP3"
                  defaultMessage="Step 3"
                />
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <FormattedMessage
                id="CONF_UPLOAD_YOUR_DATA"
                defaultMessage="Upload Your Data"
              />{' '}
              {':'}
            </Grid>

            <Grid container>
              <Grid item sm={3}>
                <UploadTemplate
                  isConfiguratorsRedesign="true"
                  accept={FileFormat.AcceptFormat}
                  handleUploadChange={([file]) => handleFileUpload(file)}
                />
              </Grid>
              {errorMessage && (
                <Grid item xs={12}>
                  <ValidationErrors
                    errors={[errorMessage]}
                    configuratorsRedesign
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        {loading && <CircleLoader />}
      </Grid>
      <CautionPopupMTORedesign
        dialogOpen={dialogOpen}
        handleClose={handleClose}
        handleContinue={handleContinue}
      />
    </Grid>
  )
}

export default MultipleProductEntry
